import React, { useState, useEffect, useRef, useCallback } from 'react';
import { testimonials } from '../../data/testimonials';
import SocialProof from '../../components/WebsiteUI/SocialProof';
import styles from './TestimonialsSection.module.css';

const TestimonialsSection = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const totalSlides = testimonials.length;
  const autoplayRef = useRef(null);
  const sectionRef = useRef(null);

  const goToSlide = useCallback((index) => {
    let newIndex = index;
    if (newIndex < 0) newIndex = totalSlides - 1;
    if (newIndex >= totalSlides) newIndex = 0;
    setCurrentIndex(newIndex);
  }, [totalSlides]);

  const startAutoplay = useCallback(() => {
    autoplayRef.current = setInterval(() => {
      goToSlide((prev) => prev + 1);
      setCurrentIndex((prev) => {
        const next = prev + 1;
        return next >= totalSlides ? 0 : next;
      });
    }, 6000);
  }, [totalSlides, goToSlide]);

  const stopAutoplay = useCallback(() => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
      autoplayRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoplay();
    return () => stopAutoplay();
  }, [startAutoplay, stopAutoplay]);

  return (
    <section
      className={styles.testimonials}
      ref={sectionRef}
      onMouseEnter={stopAutoplay}
      onMouseLeave={startAutoplay}
    >
      <div className={styles.testimonials__container}>
        <div className={styles['testimonials__social-proof']}>
          <SocialProof />
        </div>
        <div className={styles.testimonials__slider}>
          <div
            className={styles.testimonials__track}
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {testimonials.map((testimonial, index) => (
              <div
                key={testimonial.id}
                className={`${styles.testimonials__slide} ${index === currentIndex ? styles.active : ''}`}
              >
                <div className={styles.testimonials__card}>
                  <div className={styles['testimonials__card-left']}>
                    <div className={styles.testimonials__logo}>
                      <img src={testimonial.companyLogo} alt={testimonial.companyName} />
                    </div>
                  </div>
                  <div className={styles['testimonials__card-right']}>
                    <blockquote className={styles.testimonials__quote}>
                      {testimonial.quote}
                    </blockquote>
                    <div className={styles.testimonials__author}>
                      <span className={styles['testimonials__author-name']}>{testimonial.authorName}</span>
                      <span className={styles['testimonials__author-title']}>{testimonial.authorTitle}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className={styles.testimonials__nav}>
          <div className={styles.testimonials__dots}>
            {testimonials.map((_, index) => (
              <button
                key={index}
                className={`${styles.testimonials__dot} ${index === currentIndex ? styles.active : ''}`}
                onClick={() => goToSlide(index)}
                aria-label={`Go to testimonial ${index + 1}`}
              />
            ))}
          </div>
          <div className={styles.testimonials__arrows}>
            <button
              className={`${styles.testimonials__arrow} ${styles['testimonials__arrow--prev']}`}
              onClick={() => goToSlide(currentIndex - 1)}
              aria-label="Previous testimonial"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            <button
              className={`${styles.testimonials__arrow} ${styles['testimonials__arrow--next']}`}
              onClick={() => goToSlide(currentIndex + 1)}
              aria-label="Next testimonial"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
